import "ress/ress.css";
import React from "react";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { Footer } from "~/organisms/Footer";
import { InsightDownloadDocs } from "~/organisms/InsightDownloadDocs";
import { InsightInitStyle } from "~/atoms/InsightInitStyle";
import { BreadCrumb, BreadCrumbs } from "~/organisms/BreadCrumbs";
import { AskUs } from "~/molecules/IndexPageAskUs";
import { buildBreadcrumbListSchema } from "~/utils/breadcrumb";
import { colors } from "~/utils/colors";
import { breadcrumbContentGap, headerHeight } from "~/utils/variables";
import "~/utils/styledJsx";
import { Header } from "~/organisms/Header";

type Props = {
  children: React.ReactNode;
  title: string;
  description?: string;
  canonicalUrl?: string;
  ogpImageUrl?: string;
  breadCrumbItems?: BreadCrumb[];
};

export const LayoutInsight: React.FC<Props> = ({
  children,
  title,
  canonicalUrl,
  ogpImageUrl,
  description,
  breadCrumbItems,
}) => {
  const { site } = useStaticQuery<GatsbyTypes.LayoutInsightSiteMetaQuery>(graphql`
    query LayoutInsightSiteMeta {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const breadcrumbSchema =
    breadCrumbItems && breadCrumbItems.length > 0
      ? buildBreadcrumbListSchema(site.siteMetadata.siteUrl, breadCrumbItems, canonicalUrl)
      : null;

  const meta = [
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:site_name`,
      content: `Refcome`,
    },
    {
      property: `fb:app_id`,
      content: `2220166827995808`,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      httpEquiv: `content-language`,
      content: `ja`,
    },
    ...(ogpImageUrl
      ? [
          {
            property: `og:image`,
            content: ogpImageUrl,
          },
          {
            name: `twitter:image`,
            content: ogpImageUrl,
          },
        ]
      : []),
    ...(description
      ? [
          {
            name: `twitter:description`,
            content: description,
          },
          {
            name: `description`,
            content: description,
          },
          {
            property: `og:description`,
            content: description,
          },
        ]
      : []),
    ...(canonicalUrl
      ? [
          {
            property: `og:url`,
            content: canonicalUrl,
          },
        ]
      : []),
  ];

  const hasBreadCrumbs = Boolean(breadCrumbItems?.length);

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: "ja",
        }}
        meta={meta}
      >
        <title>{title}</title>
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
      </Helmet>
      <InsightInitStyle />
      <style jsx>{`
        .contentRoot {
          margin-top: ${headerHeight.desktop}px;
        }
        .contentRoot main {
          padding-bottom: 50px;
        }
        @media (max-width: 767px) {
          .contentRoot {
            margin-top: ${headerHeight.mobile}px;
          }
        }
        .contentRoot.withBreadcrumbs {
          background-color: ${colors.defaultBg};
        }
        .contentRoot.withBreadcrumbs main {
          padding-top: ${breadcrumbContentGap}px;
        }
        @media (max-height: 800px) {
          .contentRoot.withBreadcrumbs main {
            padding-top: 16px;
          }
        }
      `}</style>
      <Header />
      <div className={`contentRoot ${hasBreadCrumbs ? "withBreadcrumbs" : ""}`}>
        {hasBreadCrumbs && <BreadCrumbs items={breadCrumbItems!} bgColor={colors.defaultBg} />}
        <main>{children}</main>
      </div>
      <InsightDownloadDocs />
      <AskUs />
      <Footer />
    </>
  );
};
